import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import ItemCard from "../../components/ItemCard";
import Filters from "../../components/Filters";
import StyledCheckbox from "../../components/StyledCheckbox";

import { api } from "../../services/api";
import {
  fireDeleteMsg,
  fireSuccessMsg,
  getDateString,
  onRequestError,
} from "../../services/functions";
import { useLoading } from "../../contexts/LoadingContext";

import fileDownload from "js-file-download";

const ListUsers = () => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [csvFile, setCsvFile] = useState(null);

  const { setLoading } = useLoading();
  const location = useLocation();

  const locationRef = useRef();

  const routeName = "users";

  useEffect(() => {
    if (location && location !== locationRef.current) {
      locationRef.current = location;
      setLoading(true);
      getUsers();
    }

    // eslint-disable-next-line
  }, [location]);

  useEffect(() => {
    if (!pagination) return;
    setUsers(pagination.data);
  }, [pagination]);

  useEffect(() => {
    if (!csvFile) return;

    const importCsv = async () => {
      api
        .post(
          "/api/import-users",
          { file: csvFile },
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(response => fireSuccessMsg(response.data.msg, getUsers))
        .catch(e => {
          if (!e.caught) {
            e.msg = "Não foi possível importar o arquivo .csv!";
          }
          onRequestError(e.msg, e);
        })
        .finally(() => {
          setLoading(false);
          setCsvFile(null);
          const el = document.getElementById("inp_csv_file");
          if (el) {
            el.value = null;
          }
        });
    };

    setLoading(true);
    importCsv();

    // eslint-disable-next-line
  }, [csvFile]);

  const getUsers = async () => {
    await api
      .get(`/api/${routeName}${location.search}`)
      .then(response => {
        if (response.data.data) {
          setPagination(response.data);
        } else {
          setUsers(response.data);
          setPagination(null);
        }
      })
      .catch(e => {
        if (e.status === 404) {
          e.msg = "Não foi possível listar os usuários!";
        }
        onRequestError(e.msg, e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getMassUserNames = () => {
    let names = "";

    selected.forEach((item, i) => {
      names += item.name;
      if (i < selected.length - 1) {
        names += ", ";
      }
    });

    return names;
  };

  const onDeleteSuccess = (msg, deletedItems) => {
    fireSuccessMsg(msg);

    const usersCopy = [...users];
    deletedItems.forEach(item => {
      const index = usersCopy.indexOf(item);
      usersCopy.splice(index, 1);
    });
    setUsers(usersCopy);
    setSelected([]);
    const inputs = document.querySelectorAll("input[id^=inp_item_]:checked");
    inputs.forEach(inp => (inp.checked = false));
  };

  const deleteSingleUser = user => {
    fireDeleteMsg(user.name, async () => {
      setLoading(true);

      await api
        .delete(`/api/${routeName}/${user.id}`)
        .then(response => onDeleteSuccess(response.data.msg, [user]))
        .catch(e => onRequestError(e.msg, e))
        .finally(() => setLoading(false));
    });
  };

  const massDelete = () => {
    fireDeleteMsg(getMassUserNames(), async () => {
      setLoading(true);

      await api
        .post(`/api/${routeName}/mass-delete`, {
          ids: selected.map(item => item.id),
        })
        .then(response => onDeleteSuccess(response.data.msg, selected))
        .catch(e => onRequestError(e.msg, e))
        .finally(() => setLoading(false));
    });
  };

  const onSelectAll = e => {
    const isChecked = e.target.checked;
    const inputs = document.querySelectorAll("input[id^=inp_item_]");

    let allSelected = [];
    inputs.forEach((inp, i) => {
      inp.checked = isChecked;
      if (inp.checked) {
        allSelected = [...allSelected, users[i]];
      }
    });
    setSelected(allSelected);
  };

  const onBlobReceived = blob => {
    const fileName = `usuarios-${getDateString()}.csv`;
    fileDownload(blob, fileName);
  };

  const exportCsv = async () => {
    setLoading(true);
    await api
      .get(`/api/export-users`, { responseType: "blob" })
      .then(response => onBlobReceived(response.data))
      .catch(e => {
        if (!e.caught) {
          e.msg = "Não foi possível gerar o arquivo .csv!";
        }
        onRequestError(e.msg, e);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="h-padded v-padded-lg">
      <h1>Usuários</h1>

      <Filters pagination={pagination} />

      <div
        className="flexbox v-centered h-centered controls"
        style={{ margin: "3rem auto" }}
      >
        <Link to={`/${routeName}/register`} className="btn blue add">
          <span className="material-symbols-rounded">add</span>
          <span className="btn-text">Cadastrar novo usuário</span>
        </Link>

        <button
          className="btn blue btn-control"
          onClick={e => e.target.querySelector("input").click()}
        >
          <input
            id="inp_csv_file"
            type="file"
            hidden
            onChange={e => setCsvFile(e.target.files[0])}
            onClick={e => e.stopPropagation()}
          />
          <span className="material-symbols-rounded">upload_file</span>
          <span className="btn-text">Importar CSV</span>
        </button>

        {users.length > 0 && (
          <button className="btn blue btn-control" onClick={exportCsv}>
            <span className="material-symbols-rounded">file_save</span>
            <span className="btn-text">Exportar CSV</span>
          </button>
        )}

        {selected.length > 0 && (
          <button className="btn red btn-control" onClick={massDelete}>
            <span className="material-symbols-rounded">delete</span>
            <span className="btn-text">Excluir selecionados</span>
          </button>
        )}
      </div>

      {users.length > 0 && (
        <div className="flexbox" style={{ marginBottom: "1rem" }}>
          <StyledCheckbox
            id="inp_select_all"
            handler={onSelectAll}
            style={{ margin: "0 1rem" }}
          />
          <label htmlFor="inp_select_all" style={{ marginBottom: 0 }}>
            Selecionar todos
          </label>
        </div>
      )}

      <div>
        {users.length === 0 && (
          <span
            style={{ display: "block", marginTop: "1rem", fontSize: "22px" }}
          >
            Nenhum usuário encontrado
          </span>
        )}
        {users.map((item, i) => (
          <ItemCard
            item={item}
            index={i}
            routeName={routeName}
            deleteHandler={deleteSingleUser}
            selectionHandler={setSelected}
            selectionState={selected}
            key={i}
          />
        ))}
      </div>
    </div>
  );
};

export default ListUsers;
